import { useCallback, useEffect, useMemo, useState } from 'react'
import { Armchair, Loader2, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { canHoldSaleLines } from '@/components/pos/posHold'
import { GuestArrivalActions } from '@/components/pos/GuestArrivalActions'
import { getAssignedCustomer } from '@/components/pos/posCustomer'
import { getParentSaleLines } from '@/components/pos/posCart'
import { assignChairSession, fetchChairBoard, startChairSession } from '@/lib/api/chairBoard'
import { fetchChairs } from '@/lib/api/chairs'
import { lookupPosAppointments } from '@/lib/api/sales'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  availableChairRooms,
  findChairRoomForGuest,
  pickPosFloorAppointment,
} from '@/lib/pos/posFloor'
import { cn } from '@/lib/utils'

/**
 * POS-integrated floor ops — chair assign & check-in without leaving the ticket.
 * @param {{
 *   sale: import('@/types/api').Sale | null
 *   disabled?: boolean
 *   onHoldTicket?: () => void | Promise<void>
 * }} props
 */
export function PosFloorPanel({ sale, disabled = false, onHoldTicket }) {
  const customer = getAssignedCustomer(sale)
  const serviceLines = useMemo(
    () => getParentSaleLines(sale).filter((line) => line.line_type === 'service'),
    [sale],
  )

  const [chairs, setChairs] = useState([])
  const [appointment, setAppointment] = useState(null)
  const [board, setBoard] = useState(null)
  const [loading, setLoading] = useState(false)
  const [acting, setActing] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const today = new Date().toISOString().slice(0, 10)

  const refresh = useCallback(async () => {
    if (!customer) {
      setAppointment(null)
      setBoard(null)
      return
    }

    setLoading(true)
    setError('')

    try {
      const search = customer.phone?.trim() || customer.name?.trim() || ''
      const [chairList, appointments, nextBoard] = await Promise.all([
        fetchChairs(),
        lookupPosAppointments({ date: today, q: search || undefined }),
        fetchChairBoard(),
      ])

      setChairs(chairList)
      setBoard(nextBoard)
      setAppointment(pickPosFloorAppointment(appointments, customer.id, sale?.id))
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load floor status.'))
    } finally {
      setLoading(false)
    }
  }, [customer, sale?.id, today])

  useEffect(() => {
    refresh()
  }, [refresh])

  const activeChair = useMemo(
    () => findChairRoomForGuest(board, customer?.name ?? ''),
    [board, customer?.name],
  )

  const freeChairs = useMemo(() => availableChairRooms(board), [board])

  async function assignWalkInToChair(chairId) {
    if (!customer || !serviceLines.length) {
      return
    }

    const line = serviceLines[0]
    setActing(true)
    setError('')
    setNotice('')

    try {
      const nextBoard = await assignChairSession({
        chair_id: chairId,
        customer_id: customer.id,
        stylist_id: line.stylist_id ?? null,
        sale_line_id: line.id,
      })

      const room = nextBoard.rooms.find((item) => item.id === chairId)
      if (room?.session_id && room.session_status === 'waiting') {
        await startChairSession(room.session_id)
      }

      setNotice(`Guest sent to ${room?.name ?? 'chair'}.`)
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to assign chair.'))
    } finally {
      setActing(false)
    }
  }

  if (!customer) {
    return null
  }

  const showWalkIn = !appointment && serviceLines.length > 0
  const showPanel = appointment || showWalkIn || activeChair || loading
  const guestInService = Boolean(activeChair)
  const canHold = canHoldSaleLines(sale)

  if (!showPanel) {
    return null
  }

  return (
    <div className="mt-1.5 rounded-md border border-primary/20 bg-primary/[0.04] p-2">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-primary">
          <Armchair className="h-3.5 w-3.5" aria-hidden />
          Floor
        </p>
        <button
          type="button"
          disabled={disabled || loading || acting}
          onClick={() => refresh()}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Refresh floor status"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
        </button>
      </div>

      {loading && !appointment && !board ? (
        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </p>
      ) : null}

      {error ? <p className="mb-1.5 text-[11px] text-destructive">{error}</p> : null}
      {notice ? <p className="mb-1.5 text-[11px] text-emerald-700">{notice}</p> : null}

      {activeChair ? (
        <p className="mb-1.5 text-[11px] font-medium text-foreground">
          On {activeChair.full_name ?? activeChair.name}
          {activeChair.session_status === 'waiting' ? ' · waiting to start' : ''}
          {activeChair.session_status === 'in_chair' ? ' · in service' : ''}
        </p>
      ) : null}

      {appointment ? (
        <GuestArrivalActions
          appointment={appointment}
          chairs={chairs}
          compact
          disabled={disabled || acting}
          onUpdated={refresh}
        />
      ) : null}

      {showWalkIn && !activeChair ? (
        <div className="space-y-1.5">
          <p className="text-[11px] text-muted-foreground">Assign walk-in to a chair:</p>
          <div className="flex flex-wrap gap-1">
            {freeChairs.length === 0 ? (
              <p className="text-[11px] text-amber-700">No free chairs — check Calendar → Floor.</p>
            ) : (
              freeChairs.map((room) => (
                <Button
                  key={room.id}
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[10px]"
                  disabled={disabled || acting}
                  onClick={() => assignWalkInToChair(room.id)}
                >
                  {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : room.name}
                </Button>
              ))
            )}
          </div>
        </div>
      ) : null}

      {guestInService && canHold ? (
        <div className="mt-2 space-y-1.5 rounded-md border border-emerald-200/80 bg-emerald-50/80 p-2">
          <p className="text-[11px] leading-snug text-emerald-950">
            <strong>Pay when service is done.</strong> Hold this ticket now so you can take the next guest.
            Recall it later → <strong>F4 Pay</strong>.
          </p>
          {onHoldTicket ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 w-full border-emerald-300 bg-white text-[11px] text-emerald-900 hover:bg-emerald-50"
              disabled={disabled || acting}
              onClick={() => onHoldTicket()}
            >
              Hold ticket (F5) — pay later
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
